"use client";
import React, { useState } from "react";
import {
  Box, CircularProgress, Accordion, AccordionSummary,
  AccordionDetails, Typography, Stack, Paper, Divider
} from "@mui/material";
import { 
  ExpandMore, Add, ShieldOutlined, InfoOutlined, 
  HistoryEduOutlined 
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import {
  deleteMentalCapacityAssessment,
  getMentalCapacityAssessments,
} from "../../../../../../api/services/Assessment/ConsentCapacity";
import ConsentDialog from "./ConsentDialog";
import { QK } from "../../../../../../base/qk";
import { useParams } from "react-router-dom";
import IconCircleButton from "../../../../../../lib/Button";
import AssessmentTable from "./ConsentTable";

const ConsentCapacity = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const { clientID } = useParams();

  const { data, isLoading } = useQuery(
    [QK.consentAssessments, clientID],
    () => getMentalCapacityAssessments(clientID),
    { enabled: !!clientID }
  );

  const deleteMutation = useMutation(deleteMentalCapacityAssessment, {
    onSuccess: () => {
      enqueueSnackbar("Assessment deleted!", { variant: "success" });
      queryClient.invalidateQueries([QK.consentAssessments, clientID]);
      setShowConfirm(false);
      setDeleteId(null);
    },
    onError: () => enqueueSnackbar("Failed to delete", { variant: "error" }),
  });

  const handleEdit = (item) => {
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  return (
    <Box>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, mb: 2, borderRadius: "12px", 
          border: "1px solid #f1f5f9", bgcolor: "#f8fafc",
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ p: 1, borderRadius: "8px", bgcolor: "#fff", display: 'flex', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
            <ShieldOutlined sx={{ color: "#10b981", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: "#1e293b" }}>
              Consent Records
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8", display: 'block', lineHeight: 1 }}>
              Legal mental capacity documentation
            </Typography>
          </Box>
        </Stack>
        <IconCircleButton 
          icon={<Add sx={{ fontSize: 18 }} />} 
          onClick={() => setOpenDialog(true)} 
        />
      </Paper>

      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : (
        <Stack spacing={1.5}>
          {data?.length > 0 ? (
            data.map((item) => (
              <Accordion 
                key={item.id} 
                elevation={0}
                disableGutters
                sx={{ 
                  borderRadius: "12px !important", 
                  border: "1px solid #f1f5f9",
                  "&:before": { display: "none" },
                  bgcolor: "#fff",
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMore sx={{ fontSize: 18, color: "#94a3b8" }} />} 
                  sx={{ 
                    minHeight: 48,
                    "& .MuiAccordionSummary-content": { my: 1 },
                    "&:hover": { bgcolor: "#fcfcfd" }
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <HistoryEduOutlined sx={{ color: "#6366f1", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                      {item.consent_reason?.name || "General Consent"}
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, borderTop: "1px solid #f1f5f9" }}>
                  <Box sx={{ p: 2 }}>
                    <AssessmentTable
                      consentReasonId={item?.consent_reason?.id}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Box 
              sx={{ 
                py: 4, textAlign: 'center', borderRadius: "12px", 
                border: "1px dashed #e2e8f0", bgcolor: "#f8fafc" 
              }}
            >
              <InfoOutlined sx={{ color: "#cbd5e1", mb: 0.5 }} />
              <Typography variant="caption" sx={{ color: "#94a3b8", display: 'block', fontWeight: 600 }}>
                No capacity assessments found
              </Typography>
            </Box>
          )}
        </Stack>
      )}

      <ConsentDialog
        open={openDialog}
        handleClose={() => {
          setOpenDialog(false);
          setEditingItem(null);
        }}
        editingItem={editingItem}
      />

      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Confirm Deletion"
        body="This record will be permanently removed from compliance logs."
        isDelete
        isLoading={deleteMutation.isLoading}
      />
    </Box>
  );
};

export default ConsentCapacity;